/* @flow */
import type { ComponentType } from 'react';
import type { TFunction } from 'react-i18next';

import type { RoleList } from 'permissions/defs';
import type { Action } from 'components/ActionsList/ActionsList';
import type { TitleProp } from 'components/PageTitle';
import type { ActionButtonSpec } from 'components/TitleActionButtonRow';

import type { SummaryData } from '../ReportSummary';

export const SORT_DIRECTION_VALUES = ['ASC', 'DESC'];
export type SortDirection = 'ASC' | 'DESC';

export type ApiType = 'SQL' | 'AzureSearch';

export type SortSpec = {
  columnName: string,
  direction: SortDirection,
};

export type CellComponentProps = {
  columnName: string,
  display: *,
  rowData: *,
  t: TFunction,
};

export type DataViewColumn = {
  columnName: string,
  title: string,
  flexBasis: number,
  noFlexGrow?: boolean,
  noFlexShrink?: boolean,
  toolTip?: string,
  disableSort?: boolean,
  component?: ComponentType<CellComponentProps>,
  permissions?: RoleList,
  hidden?: boolean,
};

export type DataViewActions = { single?: Action[], multi?: Action[] } | ((data: any) => Action[]);

export type DataViewConfig = {
  dataPathname: string,
  idKey: string,
  apiType: ApiType,
  filtersPathnameOverride?: string,
  actions?: DataViewActions,
  title?: string,
  columns: DataViewColumn[],
  gridItemComponent?: ComponentType<{ rowData: Object }>,
  setColumnsVisibilityBasedOnFilters?: (
    columns: DataViewColumn[],
    filters: ConsolidatedFilterItem[]
  ) => void,
};

export type SupportedUriTokenNames = 'brandId' | 'accountId';
export type SummaryViewConfig = {
  title: TitleProp,
  sectionHeading: string,
  dataPathname: string,
  buttons?: ActionButtonSpec[],
  sectionHeadingButtons?: ActionButtonSpec[],
  useUriTokens?: SupportedUriTokenNames[],
  enableRegionSelect?: boolean,
  excludeFilterKeysFromReportView?: string[],
  logoPath?: string[],
  mapDataToLayout: (data: Object) => SummaryData,
};

export type ServerFilterType = 'string' | 'date' | 'boolean';
export type AvailableFilterOption = {
  label: string,
  value: string,
  from?: string,
  to?: string,
};
export type AvailableGroupedFilterOption = {
  name: string,
  label: string,
  value: string,
  key: string,
  isGroup?: boolean,
};

export type AvailableGroupedFilter = {
  [key: string]: AvailableGroupedFilterOption[],
};

export type AvailableFilter = {
  /**
   * This is the column identifier
   */
  key: string,
  name: string,
  type: ServerFilterType,
  filters: AvailableFilterOption[],
  groupedFilters?: AvailableGroupedFilter,
};

export type BaseFilterItem = {
  /**
   * This is the column identifier
   */
  key: string,
  values: string[],
};

export type URLFilterItem = BaseFilterItem & {
  /**
   * Force remove even if implicit
   */
  forceRemove?: boolean,
  /**
   * Whether or not the filter should be un-removable
   */
  fixed?: boolean,
  /**
   * Implicit filters are those that are set via the User Search Preference, or table default. A URL filter with this flag set to true will override the matching filter from those sources.
   */
  overrideImplicit?: boolean,
};

export type UserFilterItem = {
  /**
   * This is the URL/column identifier
   */
  key: string,
  type: ServerFilterType,
  value: string,
  // TODO: Check whether or not we need a label here
};

export type UserSearchPreference = {
  id: string,
  createdAt: string,
  model: string,
  name: string,
  searchConfig: {
    filters: UserFilterItem[],
    search: string[],
  },
  updatedAt: string,
  userId: string,
};

export type ConsolidatedFilterItem = BaseFilterItem & {
  name?: string,
  type?: ServerFilterType,
  isFixed?: boolean,
  userFilterId?: string,
  isDefault?: boolean,
  doesNotMatchAvailableFilters?: boolean,
  overridesImplicit?: boolean,
  labels?: { [string]: string },
};

export type ViewType = 'TABBED' | 'SINGLE';
export type LayoutType = 'TABLE' | 'GRID';

export type ViewConfigBase = {
  /**
   * The codename is used by anything that needs to identify the table type programmatically
   */
  codename: string,
  title: string,
  /**
   * The namespace used to query for and update user filters for this table
   */
  userFilterNamespace: string,
  /**
   * Defaults to 'TABLE'
   */
  supportedLayouts?: LayoutType[],
  defaultLayout?: LayoutType,
  defaultFilters?: URLFilterItem[],
  defaultSearch?: string,
  defaultSort?: SortSpec,
  buttons?: ActionButtonSpec[],
  disableFilters?: boolean,
  disableFilterChips?: boolean,
  disableDownload?: boolean,
  disabledSelectAll?: boolean,
  disableUserPreferenceSave?: boolean,
  downloadPermissions?: RoleList,
};

export type ViewConfig = ViewConfigBase &
  (
    | {
        type: 'TABBED',
        views: { [string]: DataViewConfig },
        defaultTab?: string,
      }
    | {
        type: 'SINGLE',
        view: DataViewConfig,
      }
    | {
        type: 'SINGLE_WITH_SUMMARY',
        view: DataViewConfig,
        summaryView: SummaryViewConfig,
      }
  );

export type SelectionDetails = {
  selectedItems: any[],
};
